import { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckIcon from '@mui/icons-material/Check';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { useTranslation } from 'react-i18next';

import { SYSTEMS, normalizeDateSystem, formatDate, monthNames, today, toIsoDay } from '../calendars';
import {
  monthGrid, shiftMonth, shiftYear, withMonth, withYear, yearPage, weekdayNames, sameDay, addDays, rangeDays,
} from './calendarGrid';
import { timeFramePresets } from '../../components/mis/packingLists/timeFrames';
import { useTools } from '../../contextApi/toolsContext';
import { useBranch } from '../../contextApi/BranchContext';
import { useToolTokens, keepFocus, useFocusOnOpen } from './toolStyles';

// The floating calendar. Three calendars (Gregorian, Shamsi, Hijri) over ONE selected range: the range is kept as
// absolute days, so switching calendar re-draws the same days, and the table under the grid shows the selection in all
// three at once. Where the screen underneath has a date filter (see useDateFilterTarget) a button applies the range to it.
//
// Its state - the calendar chosen, the month showing, the picked range - lives in ToolsProvider, so it is still here when
// the window is closed and opened again.

const SYSTEM_LABEL_KEYS = { gregorian: 'tools.calGregorian', jalali: 'tools.calJalali', hijri: 'tools.calHijri' };
const PRESET_KEYS = ['today', 'last7', 'last30', 'thisMonth'];

const reset = { border: 0, m: 0, p: 0, font: 'inherit', background: 'none', cursor: 'pointer', touchAction: 'manipulation' };

export default function CalendarTool({ size, mobile, onClose }) {
  const T = useToolTokens();
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'en').slice(0, 2);
  const { cal, patchCal, target } = useTools();
  const { activeBranch } = useBranch();
  const rootRef = useRef(null);
  useFocusOnOpen(rootRef);

  // The calendar the person last chose; until they choose, the active branch's own.
  const system = normalizeDateSystem(cal.system || (activeBranch && activeBranch.defaultDateSystem));
  const { cursor, from, to } = cal;
  const last = to || from;
  const isRange = Boolean(from && to && !sameDay(from, to));

  const view = cal.view;                           // 'days' | 'months' | 'years' - kept in ToolsProvider, so closing the window keeps it
  const setView = (v) => patchCal({ view: v });
  const [kbd, setKbd] = useState(false);            // the arrow keys are being used: draw the cursor day
  const [notice, setNotice] = useState('');
  const noticeTimer = useRef(null);
  useEffect(() => () => clearTimeout(noticeTimer.current), []);

  const todayDay = today();
  const grid = useMemo(() => monthGrid(cursor, system), [cursor, system]);
  const months = useMemo(() => monthNames(system, lang), [system, lang]);
  const weekdays = useMemo(() => weekdayNames(system, lang, lang === 'en' ? 'short' : 'narrow'), [system, lang]);
  const page = useMemo(() => yearPage(grid.year), [grid.year]);
  const presets = useMemo(() => timeFramePresets(system).filter((p) => PRESET_KEYS.includes(p.key)), [system]);

  const flash = (kind) => {
    setNotice(kind);
    clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(''), 2600);
  };

  const pick = (date) => {
    if (!from || to) { patchCal({ from: date, to: null, cursor: date }); return; }        // a new range starts
    const [a, b] = date.getTime() < from.getTime() ? [date, from] : [from, date];         // the second click ends it, either way round
    patchCal({ from: a, to: b, cursor: date });
  };
  const step = (dir) => {
    if (view === 'days') patchCal({ cursor: shiftMonth(cursor, system, dir) });
    else if (view === 'months') patchCal({ cursor: shiftYear(cursor, system, dir) });
    else patchCal({ cursor: shiftYear(cursor, system, dir * 12) });
  };
  const choosePreset = (p) => {
    const a = new Date(p.from);
    const b = new Date(p.to);
    patchCal({ from: a, to: sameDay(a, b) ? null : b, cursor: b });
    setView('days');
  };

  const apply = () => {
    if (!target || !from) return;
    target.apply({ from: toIsoDay(from), to: toIsoDay(last), system });
    if (mobile) onClose(); else flash('applied');        // on a phone the list is behind this page: close it so the result is the first thing seen
  };
  const clearFilter = () => {
    if (!target) return;
    target.apply({ from: '', to: '', system });
    if (mobile) onClose(); else flash('cleared');
  };

  const onKeyDown = (e) => {
    if (view !== 'days' || e.ctrlKey || e.metaKey || e.altKey) return;
    const fromDay = e.target === e.currentTarget || (e.target.closest && e.target.closest('[data-day]'));
    let next = null;
    switch (e.key) {
      case 'ArrowLeft': next = addDays(cursor, T.rtl ? 1 : -1); break;     // the grid reads right to left in Persian and Arabic
      case 'ArrowRight': next = addDays(cursor, T.rtl ? -1 : 1); break;
      case 'ArrowUp': next = addDays(cursor, -7); break;
      case 'ArrowDown': next = addDays(cursor, 7); break;
      case 'PageUp': next = e.shiftKey ? shiftYear(cursor, system, -1) : shiftMonth(cursor, system, -1); break;
      case 'PageDown': next = e.shiftKey ? shiftYear(cursor, system, 1) : shiftMonth(cursor, system, 1); break;
      case 't': case 'T': next = todayDay; break;
      case 'Enter': case ' ':
        if (!fromDay) return;                            // on a button (Today, a preset, Apply...) Enter means that button
        e.preventDefault(); setKbd(true); pick(cursor); return;
      default: return;
    }
    e.preventDefault();
    setKbd(true);
    patchCal({ cursor: next });
  };

  const prevIcon = T.rtl ? <ChevronRightIcon /> : <ChevronLeftIcon />;
  const nextIcon = T.rtl ? <ChevronLeftIcon /> : <ChevronRightIcon />;
  const prevLabel = view === 'days' ? t('tools.prevMonth') : view === 'months' ? t('tools.prevYear') : t('tools.prevYears');
  const nextLabel = view === 'days' ? t('tools.nextMonth') : view === 'months' ? t('tools.nextYear') : t('tools.nextYears');
  const title = view === 'days' ? `${months[grid.month - 1]} ${grid.year}` : view === 'months' ? String(grid.year) : `${page.start} – ${page.start + 11}`;

  const cellMin = mobile ? 44 : size.h < 520 ? 28 : 32;
  const dayFont = mobile ? '0.95rem' : size.w > 420 ? '0.9rem' : '0.8rem';

  const navButton = (label, icon, dir) => (
    <IconButton size="small" aria-label={label} onMouseDown={keepFocus} onClick={() => step(dir)}
      sx={{ width: mobile ? 40 : 30, height: mobile ? 40 : 30, color: T.TEXT2, '&:hover': { color: T.TEXT, bgcolor: T.CTRL_HOVER } }}>
      {icon}
    </IconButton>
  );

  const pickerCell = (key, label, active, onClick) => (
    <Box key={key} component="button" type="button" onMouseDown={keepFocus} onClick={onClick}
      sx={{
        ...reset, borderRadius: '10px', fontSize: mobile ? '0.95rem' : '0.82rem', fontWeight: active ? 700 : 500,
        color: active ? T.ON_ACCENT : T.TEXT, bgcolor: active ? T.ACCENT : T.CTRL, minHeight: mobile ? 48 : 38,
        '&:hover': { bgcolor: active ? T.ACCENT : T.CTRL_HOVER },
      }}>
      {label}
    </Box>
  );

  const rowLabel = { fontSize: '0.68rem', color: T.TEXT2, fontWeight: 600 };
  const rowValue = { fontSize: '0.78rem', color: T.TEXT, fontWeight: 600, direction: 'ltr', textAlign: T.rtl ? 'right' : 'left', fontVariantNumeric: 'tabular-nums' };

  return (
    <Box
      ref={rootRef} tabIndex={-1} onKeyDown={onKeyDown} onPointerDown={() => setKbd(false)}
      data-tool="calendar"
      sx={{
        height: '100%', display: 'flex', flexDirection: 'column', gap: 1.25, overflowY: 'auto', outline: 'none',
        // no padding under the window's last block: the pinned Apply / Clear block has its own, so it sits flush with the bottom edge
        ...(mobile ? { p: 2 } : { pt: 1.5, px: 1.5, pb: 0 }), '& > *': { flexShrink: 0 },
      }}
    >
      {/* which calendar */}
      <Box role="tablist" aria-label={t('tools.calendar')}
        sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.5, p: 0.5, bgcolor: T.CTRL, borderRadius: '10px' }}>
        {SYSTEMS.map((s) => {
          const on = s === system;
          return (
            <Box key={s} component="button" type="button" role="tab" aria-selected={on} data-system={s}
              onMouseDown={keepFocus} onClick={() => { patchCal({ system: s }); setView('days'); }}
              sx={{
                ...reset, height: mobile ? 38 : 28, borderRadius: '8px', fontSize: mobile ? '0.85rem' : '0.78rem', fontWeight: on ? 700 : 500,
                color: on ? T.ON_ACCENT : T.TEXT2, bgcolor: on ? T.ACCENT : 'transparent',
                '&:hover': { bgcolor: on ? T.ACCENT : T.CTRL_HOVER, color: on ? T.ON_ACCENT : T.TEXT },
              }}>
              {t(SYSTEM_LABEL_KEYS[s])}
            </Box>
          );
        })}
      </Box>

      {/* month / year header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {navButton(prevLabel, prevIcon, -1)}
        <Box component="button" type="button" data-tool-title onMouseDown={keepFocus}
          onClick={() => setView(view === 'days' ? 'months' : view === 'months' ? 'years' : 'days')}
          sx={{
            ...reset, flexGrow: 1, height: mobile ? 40 : 30, borderRadius: '8px', color: T.TEXT, fontWeight: 700,
            fontSize: mobile ? '1rem' : '0.86rem', '&:hover': { bgcolor: T.CTRL_HOVER },
          }}>
          {title}
        </Box>
        {navButton(nextLabel, nextIcon, 1)}
        <Button size="small" onMouseDown={keepFocus} onClick={() => { patchCal({ cursor: todayDay }); setView('days'); }}
          sx={{ minWidth: 0, height: mobile ? 40 : 30, px: 1.25, fontSize: '0.74rem', textTransform: 'none', color: T.TEXT, border: `1px solid ${T.BD2}`, borderRadius: '8px' }}>
          {t('tools.today')}
        </Button>
      </Box>

      {/* the days / months / years */}
      {view === 'days' && (
        <Box sx={{ flex: '1 0 auto', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 0.5 }}>
            {weekdays.map((w, i) => (
              <Typography key={i} sx={{ textAlign: 'center', fontSize: '0.66rem', fontWeight: 700, color: T.TEXT3, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{w}</Typography>
            ))}
          </Box>
          <Box data-grid role="grid" sx={{ flexGrow: 1, display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridTemplateRows: `repeat(6, minmax(${cellMin}px, 1fr))`, rowGap: '2px' }}>
            {grid.cells.map((cell) => {
              const isEnd = sameDay(cell.date, from) || sameDay(cell.date, last);
              const inside = Boolean(from) && !isEnd && cell.date.getTime() > from.getTime() && cell.date.getTime() < last.getTime();
              const isToday = sameDay(cell.date, todayDay);
              const isCursor = kbd && sameDay(cell.date, cursor);
              return (
                <Box key={cell.date.getTime()} component="button" type="button" role="gridcell" tabIndex={-1}
                  data-day={toIsoDay(cell.date)} data-in-month={cell.inMonth ? '1' : '0'}
                  data-selected={isEnd ? '1' : inside ? 'range' : '0'} data-today={isToday ? '1' : '0'}
                  aria-selected={isEnd || inside}
                  onMouseDown={keepFocus} onClick={() => pick(cell.date)}
                  sx={{
                    ...reset, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: dayFont,
                    fontWeight: isEnd ? 700 : 500, fontVariantNumeric: 'tabular-nums',
                    color: isEnd ? T.ON_ACCENT : cell.inMonth ? T.TEXT : T.TEXT3,
                    bgcolor: isEnd ? T.ACCENT : inside ? T.RANGE : 'transparent',
                    borderRadius: inside ? 0 : '8px',
                    boxShadow: isToday && !isEnd ? `inset 0 0 0 1.5px ${T.TEXT2}` : 'none',
                    outline: isCursor ? `2px solid ${T.TEXT2}` : 'none', outlineOffset: -2,
                    '&:hover': { bgcolor: isEnd ? T.ACCENT : T.CTRL_HOVER },
                  }}>
                  {cell.day}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
      {view === 'months' && (
        <Box sx={{ flex: '1 0 auto', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gridAutoRows: 'minmax(44px, 1fr)', gap: 0.75 }}>
          {months.map((name, i) => pickerCell(i, name, i + 1 === grid.month, () => { patchCal({ cursor: withMonth(cursor, system, i + 1) }); setView('days'); }))}
        </Box>
      )}
      {view === 'years' && (
        <Box sx={{ flex: '1 0 auto', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gridAutoRows: 'minmax(44px, 1fr)', gap: 0.75 }}>
          {page.years.map((y) => pickerCell(y, String(y), y === grid.year, () => { patchCal({ cursor: withYear(cursor, system, y) }); setView('months'); }))}
        </Box>
      )}

      {/* what is selected, in all three calendars */}
      <Box data-selection sx={{ border: `1px solid ${T.BD}`, borderRadius: '10px', p: 1, minHeight: 36 }}>
        {from ? (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: isRange ? 'auto 1fr 1fr' : 'auto 1fr', columnGap: 1.5, rowGap: 0.35, alignItems: 'baseline' }}>
              <span />
              <Typography sx={rowLabel}>{t('tools.from')}</Typography>
              {isRange && <Typography sx={rowLabel}>{t('tools.to')}</Typography>}
              {SYSTEMS.map((s) => [
                <Typography key={`${s}-l`} sx={{ ...rowLabel, fontWeight: s === system ? 800 : 500, color: s === system ? T.TEXT : T.TEXT2 }}>{t(SYSTEM_LABEL_KEYS[s])}</Typography>,
                <Typography key={`${s}-f`} data-sel={`${s}-from`} sx={{ ...rowValue, fontWeight: s === system ? 800 : 600 }}>{formatDate(from, s, { tag: false })}</Typography>,
                isRange && <Typography key={`${s}-t`} data-sel={`${s}-to`} sx={{ ...rowValue, fontWeight: s === system ? 800 : 600 }}>{formatDate(to, s, { tag: false })}</Typography>,
              ])}
            </Box>
            {isRange && <Typography data-sel="days" sx={{ mt: 0.75, fontSize: '0.7rem', color: T.TEXT2 }}>{t('tools.days', { n: rangeDays(from, to) })}</Typography>}
          </>
        ) : (
          <Typography sx={{ fontSize: '0.74rem', color: T.TEXT2, lineHeight: 1.5 }}>{t('tools.pickHint')}</Typography>
        )}
      </Box>

      {/* ready-made ranges, in the calendar being used */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {presets.map((p) => (
          <Box key={p.key} component="button" type="button" data-preset={p.key} onMouseDown={keepFocus} onClick={() => choosePreset(p)}
            sx={{ ...reset, px: 1.25, height: mobile ? 36 : 26, borderRadius: '999px', fontSize: '0.72rem', color: T.TEXT, bgcolor: T.CTRL, '&:hover': { bgcolor: T.CTRL_HOVER } }}>
            {t(p.labelKey)}
          </Box>
        ))}
      </Box>

      {/* the screen underneath, if it has a date filter */}
      {/* This block is PINNED to the bottom of the window (and of the phone's page): it is the point of the calendar, and in the
          flow it sat below the fold - a range picked in a default-sized window pushed "Clear the date filter" out of reach, and
          a click there landed on whatever the window was covering. Only the part above it scrolls. */}
      <Box data-apply sx={{
        display: 'flex', flexDirection: 'column', gap: 0.75,
        position: 'sticky', bottom: 0, mt: 'auto', zIndex: 1, bgcolor: T.BG, borderTop: `1px solid ${T.BD}`,
        ...(mobile
          ? { mx: -2, px: 2, pt: 1, pb: 'max(8px, env(safe-area-inset-bottom))' }
          : { mx: -1.5, px: 1.5, pt: 1, pb: 1.5 }),
      }}>
        {target ? (
          <>
            <Button variant="contained" size="small" fullWidth disabled={!from} onMouseDown={keepFocus} onClick={apply}
              data-apply-button startIcon={<FilterAltIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none', fontWeight: 700, height: mobile ? 44 : 34, borderRadius: '10px' }}>
              {t('tools.applyTo', { section: target.label })}
            </Button>
            {target.detail && <Typography sx={{ fontSize: '0.68rem', color: T.TEXT2, textAlign: 'center' }}>{t('tools.appliesTo', { what: target.detail })}</Typography>}
            <Button size="small" fullWidth onMouseDown={keepFocus} onClick={clearFilter} data-clear-button
              startIcon={<FilterAltOffIcon sx={{ fontSize: 15 }} />}
              sx={{ textTransform: 'none', color: T.TEXT2, height: mobile ? 40 : 28, fontSize: '0.74rem' }}>
              {t('tools.clearFilter')}
            </Button>
          </>
        ) : (
          <Typography data-no-target sx={{ fontSize: '0.72rem', color: T.TEXT3, textAlign: 'center' }}>{t('tools.noDateFilter')}</Typography>
        )}
        <Box role="status" aria-live="polite" sx={{ minHeight: 18, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5, color: T.TEXT, fontSize: '0.74rem', fontWeight: 600 }}>
          {notice && <><CheckIcon sx={{ fontSize: 15 }} /><span data-notice={notice}>{notice === 'applied' ? t('tools.applied') : t('tools.cleared')}</span></>}
        </Box>
      </Box>
    </Box>
  );
}
